// src/App.js
//
// Phase 6 — Code-split routes + multi-tenant signup + admin console.
// ---------------------------------------------------------------------
// Every non-essential page is loaded lazily via React.lazy so the
// initial bundle stays small. Only Login / Register / Signup ship
// eagerly because they are the entry points for unauthenticated users.

import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box, CircularProgress } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AppThemeProvider } from './contexts/ThemeContext';
import { FontProvider } from './contexts/FontContext';
import RootErrorBoundary from './components/RootErrorBoundary';
import PermissionDeniedToast from './components/PermissionDeniedToast';
import { isParentRole, isStaffRole, ROLES } from './services/userService';
import './i18n';

import Layout from './components/Layout';
import Welcome from './components/Welcome';
import Login from './components/Login';
import Register from './components/Register';
import Signup from './components/Signup';

const Dashboard = React.lazy(() => import('./components/Dashboard'));
const ClassesManagement = React.lazy(() => import('./components/ClassesManagement'));
const StudentsManagement = React.lazy(() => import('./components/StudentsManagement'));
const StudentProfile = React.lazy(() => import('./components/StudentProfile'));
const DailyProgress = React.lazy(() => import('./components/DailyProgress'));
const RevisionQueue = React.lazy(() => import('./components/RevisionQueue'));
const MemorizationHealth = React.lazy(() => import('./components/MemorizationHealth'));
const Attendance = React.lazy(() => import('./components/Attendance'));
const CurriculumPlanner = React.lazy(() => import('./components/CurriculumPlanner'));
const Reports = React.lazy(() => import('./components/Reports'));
const Certificates = React.lazy(() => import('./components/Certificates'));
const VerifyCertificate = React.lazy(() => import('./components/VerifyCertificate'));
const TenantAdmin = React.lazy(() => import('./components/admin/TenantAdmin'));
const DirectorDashboard = React.lazy(() => import('./components/DirectorDashboard'));
const SttTestLab = React.lazy(() => import('./components/admin/SttTestLab'));
const ParentDashboard = React.lazy(() => import('./components/parent/ParentDashboard'));
const ParentChildView = React.lazy(() => import('./components/parent/ParentChildView'));
const PrintableParentReport = React.lazy(() => import('./components/parent/PrintableParentReport'));
const ParentalConsent = React.lazy(() => import('./components/parent/ParentalConsent'));
const ChatList = React.lazy(() => import('./components/chat/ChatList'));
const ChatThread = React.lazy(() => import('./components/chat/ChatThread'));
const StudentHome = React.lazy(() => import('./components/student/StudentHome'));
const QuizEngine = React.lazy(() => import('./components/student/QuizEngine'));
const ReciterPartner = React.lazy(() => import('./components/student/ReciterPartner'));
const TadabburJournal = React.lazy(() => import('./components/student/TadabburJournal'));
const TadabburBook = React.lazy(() => import('./components/student/TadabburBook'));
const TeacherProfile = React.lazy(() => import('./components/TeacherProfile'));
const LinkAccount = React.lazy(() => import('./components/LinkAccount'));
const MemorizationSuggestionsQueue = React.lazy(() =>
  import('./components/MemorizationSuggestionsQueue'),
);

const RouteFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
    <CircularProgress />
  </Box>
);

const homePathForRole = (role) => {
  if (role === ROLES.STUDENT) return '/student/home';
  if (isParentRole(role)) return '/parent/dashboard';
  return '/dashboard';
};

const ProtectedRoute = ({ children, allow }) => {
  const { currentUser, role, authReady } = useAuth();
  // Wait for Firebase to rehydrate the auth state before making a routing
  // decision. Without this, a full-page refresh flashes /login briefly.
  if (!authReady) {
    return <RouteFallback />;
  }
  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }
  if (allow === 'parent' && !isParentRole(role)) {
    return <Navigate to={homePathForRole(role)} replace />;
  }
  if (allow === 'staff' && !isStaffRole(role)) {
    return <Navigate to={homePathForRole(role)} replace />;
  }
  if (allow === 'admin' && role !== ROLES.ADMIN) {
    return <Navigate to={homePathForRole(role)} replace />;
  }
  if (allow === 'student' && role !== ROLES.STUDENT) {
    return <Navigate to={homePathForRole(role)} replace />;
  }
  return children;
};

const HomeRedirect = () => {
  const { role } = useAuth();
  return <Navigate to={homePathForRole(role)} replace />;
};

const PublicHome = () => {
  const { currentUser, role, authReady } = useAuth();
  if (!authReady) return <RouteFallback />;
  if (currentUser) {
    return <Navigate to={homePathForRole(role)} replace />;
  }
  return <Welcome />;
};

const PublicOnly = ({ children }) => {
  const { currentUser, role, authReady } = useAuth();
  if (!authReady) return <RouteFallback />;
  if (currentUser) {
    return <Navigate to={homePathForRole(role)} replace />;
  }
  return children;
};

// S8 — Sacred Canvas route cross-fade. Each navigation re-keys the
// wrapper, replaying the .route-mount opacity-only fade-in defined
// in MuiCssBaseline. prefers-reduced-motion users get a no-op there.
const RouteFade = ({ children }) => {
  const location = useLocation();
  return (
    <div key={location.pathname} className="route-mount">
      {children}
    </div>
  );
};

function App() {
  return (
    <RootErrorBoundary>
      <FontProvider>
      <AppThemeProvider>
        {(theme) => (
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <div className="App" dir="rtl">
              <PermissionDeniedToast />
              <Router>
                <AuthProvider>
                  <Suspense fallback={<RouteFallback />}>
                    <RouteFade>
                    <Routes>
                      <Route path="/" element={<PublicHome />} />
                      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
                      <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
                      <Route path="/signup" element={<PublicOnly><Signup /></PublicOnly>} />
                      <Route path="/verify/:code" element={<VerifyCertificate />} />

                      {/* Post-phone-signup waiting page — any signed-in user with or without a tenant. */}
                      <Route
                        path="/link-account"
                        element={
                          <ProtectedRoute>
                            <LinkAccount />
                          </ProtectedRoute>
                        }
                      />

                      {/* Staff (admin / teacher) routes */}
                      <Route
                        element={
                          <ProtectedRoute allow="staff">
                            <Layout />
                          </ProtectedRoute>
                        }
                      >
                        <Route path="/dashboard" element={<Dashboard />} />
                        <Route path="/classes" element={<ClassesManagement />} />
                        <Route path="/students" element={<StudentsManagement />} />
                        <Route path="/students/:id" element={<StudentProfile />} />
                        <Route path="/daily-progress" element={<DailyProgress />} />
                        <Route path="/revision-queue" element={<RevisionQueue />} />
                        <Route path="/memorization-health" element={<MemorizationHealth />} />
                        <Route path="/attendance" element={<Attendance />} />
                        <Route path="/curriculum" element={<CurriculumPlanner />} />
                        <Route path="/certificates" element={<Certificates />} />
                        <Route path="/reports" element={<Reports />} />
                        <Route path="/chat" element={<ChatList />} />
                        <Route path="/chat/:id" element={<ChatThread />} />
                        <Route path="/profile" element={<TeacherProfile />} />
                      </Route>

                      {/* Student self-study routes */}
                      <Route
                        element={
                          <ProtectedRoute allow="student">
                            <Layout />
                          </ProtectedRoute>
                        }
                      >
                        <Route path="/student/home" element={<StudentHome />} />
                        <Route path="/student/quiz" element={<QuizEngine />} />
                        <Route path="/student/recite" element={<ReciterPartner />} />
                        <Route path="/student/review" element={<RevisionQueue />} />
                        <Route path="/student/tadabbur" element={<TadabburJournal />} />
                        <Route path="/student/tadabbur/book" element={<TadabburBook />} />
                      </Route>

                      {/* Admin-only routes */}
                      <Route
                        element={
                          <ProtectedRoute allow="admin">
                            <Layout />
                          </ProtectedRoute>
                        }
                      >
                        <Route path="/admin" element={<TenantAdmin />} />
                        <Route path="/admin/stt-lab" element={<SttTestLab />} />
                        <Route path="/director" element={<DirectorDashboard />} />
                        {/* almada §3.2 — moudir's auto-promotion review queue. */}
                        <Route
                          path="/students/suggestions"
                          element={<MemorizationSuggestionsQueue />}
                        />
                      </Route>

                      {/* Parent portal routes */}
                      <Route
                        element={
                          <ProtectedRoute allow="parent">
                            <Layout />
                          </ProtectedRoute>
                        }
                      >
                        <Route path="/parent/dashboard" element={<ParentDashboard />} />
                        <Route path="/parent/reports" element={<ParentDashboard />} />
                        {/* almada §5.1 — bottom tab "الشهادات" alias. */}
                        <Route path="/parent/certificates" element={<ParentDashboard />} />
                        <Route path="/parent/child/:id" element={<ParentChildView />} />
                        <Route path="/parent/report/:id" element={<PrintableParentReport />} />
                        <Route path="/parent/consent" element={<ParentalConsent />} />
                        <Route path="/parent/chat" element={<ChatList />} />
                        <Route path="/parent/chat/:id" element={<ChatThread />} />
                      </Route>

                      <Route
                        path="*"
                        element={
                          <ProtectedRoute>
                            <HomeRedirect />
                          </ProtectedRoute>
                        }
                      />
                    </Routes>
                    </RouteFade>
                  </Suspense>
                </AuthProvider>
              </Router>
            </div>
          </ThemeProvider>
        )}
      </AppThemeProvider>
      </FontProvider>
    </RootErrorBoundary>
  );
}

export default App;
